
import { toast as sonnerToast } from "sonner";

type ToastProps = {
  title?: string;
  description?: string;
  variant?: "default" | "destructive";
  [key: string]: any;
};

// Create a wrapper function that adapts our toast usage to sonner's API
export const useToast = () => {
  return {
    toast: (props: ToastProps) => {
      const { title, description, variant, ...rest } = props;
      
      // Combine title and description for sonner
      const message = description || title || "";
      
      if (variant === "destructive") {
        return sonnerToast.error(message, {
          ...rest
        });
      }
      
      return sonnerToast(message, {
        ...rest
      });
    },
    // Add a fake toasts array to satisfy the existing toaster component
    toasts: []
  };
};

// Create a compatibleToast function that can be called directly
const compatibleToast = (props: ToastProps | string) => {
  if (typeof props === 'string') {
    return sonnerToast(props);
  } else {
    const { title, description, variant, ...rest } = props;
    const message = description || title || '';
    
    if (variant === "destructive") {
      return sonnerToast.error(message, { ...rest });
    }
    
    return sonnerToast(message, { ...rest });
  }
};

// Create a wrapper for direct toast usage
export const toast = Object.assign(
  compatibleToast,
  {
    // Basic toast methods
    success: (message: string | ToastProps, options?: any) => {
      if (typeof message === 'string') {
        return sonnerToast.success(message, options);
      } else {
        const content = message.description || message.title || '';
        return sonnerToast.success(content, options);
      }
    },
    error: (message: string | ToastProps, options?: any) => {
      if (typeof message === 'string') {
        return sonnerToast.error(message, options);
      } else {
        const content = message.description || message.title || '';
        return sonnerToast.error(content, options);
      }
    },
    warning: (message: string | ToastProps, options?: any) => {
      if (typeof message === 'string') {
        return sonnerToast.warning(message, options);
      } else {
        const content = message.description || message.title || '';
        return sonnerToast.warning(content, options);
      }
    },
    info: (message: string | ToastProps, options?: any) => {
      if (typeof message === 'string') {
        return sonnerToast(message, options);
      } else {
        const content = message.description || message.title || '';
        return sonnerToast(content, options);
      }
    },
    // Compatibility with our previous toast API
    default: (message: string | ToastProps, options?: any) => {
      if (typeof message === 'string') {
        return sonnerToast(message, options);
      } else {
        const content = message.description || message.title || '';
        return sonnerToast(content, options);
      }
    },
    destructive: (message: string | ToastProps, options?: any) => {
      if (typeof message === 'string') {
        return sonnerToast.error(message, options);
      } else {
        const content = message.description || message.title || '';
        return sonnerToast.error(content, options);
      }
    }
  }
);
